import { useCallback } from "react";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { GlobalJSBridgePayload, GlobalJSBridgeType } from "@swiggy-private/cart-types";
import { isEmpty, safeJsonParse } from "@swiggy-private/js-utils";
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";

type CallBckRefType = {
    [key in GlobalJSBridgeType]: Function | undefined;
};

// Shared across all hook instances to avoid one overriding the other.
// registers into one map and one window dispatcher.
// forms a closure around the callback map so that it is not overridden by subsequent hook instances.

const callbackMap: CallBckRefType = {} as CallBckRefType;

const hasActiveCallbacks = (): boolean =>
    Object.keys(callbackMap).filter(
        (key) => typeof callbackMap[key as GlobalJSBridgeType] === "function",
    ).length > 0;

const jsBridgeCallbackHandler = (data: string) => {
    try {
        const payload = safeJsonParse<GlobalJSBridgePayload>(data) || { type: GlobalJSBridgeType.INVALID };
        if (!isEmpty(payload?.type) && typeof callbackMap[payload?.type] === "function") {
            return callbackMap[payload?.type]?.(payload);
        }
        throw new Error("Incorrect Payload Type or No Handler for this Payload Type");
    } catch (e) {
        recordNREvent({
            eventType: NEWRELIC_TABLES.GLOBAL_JS_BRIDGE_INCORRECT_TRIGGER,
            payload: data || "",
            errMessage: (e as Error)?.message || "",
        });
    }
};

export const useGlobalJSBridgeHandler = () => {
    const registerCallbackForType = useCallback((type: GlobalJSBridgeType, handler: Function) => {
        callbackMap[type] = handler;
        // Re-install in case a prior unmount tore down the dispatcher while another instance is alive.
        window.globalJSBridgeHandler = jsBridgeCallbackHandler;
    }, []);

    const unregisterCallbackForType = useCallback((type: GlobalJSBridgeType, handler?: Function) => {
        if (typeof handler === "function") {
            callbackMap[type] = handler;
        } else {
            callbackMap[type] = undefined;
        }
    }, []);

    useEffectOnce(() => {
        window.globalJSBridgeHandler = jsBridgeCallbackHandler;
        return () => {
            // Tear down only when no registered handlers remain (shared across hook instances).
            if (!hasActiveCallbacks()) {
                window.globalJSBridgeHandler = () => void 0;
            }
        };
    });

    return {
        registerCallbackForType,
        unregisterCallbackForType,
    };
};
